import {
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableSortLabel,
    TablePagination,
    Typography, Stack,
} from "@mui/material";
import React from "react";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {styled, useTheme} from "@mui/material/styles";
import {tableCellClasses} from "@mui/material/TableCell";
import {displayDateTime} from "../../../../utils/formatDate.ts";
import {Trans} from "@lingui/react/macro";
import CuttingName from "../../cutting/CuttingDetail/CuttingName.tsx";
import {OrderBy, OrderDirection} from "../../../../types/global.d";
import {
    BigTableHeaderRow, BigTableRow,
    SmallTableHeaderRow,
    SmallTableRow,
    StyledFillNavLink, StyledMdiIcon
} from "../../../../share/components/styled/Table.tsx";
import {PrimaryShipment, Shipment} from "../../../types/models/Shipment.ts";
import BuyerName from "../../buyer/BuyerDetail/BuyerName.tsx";
import {toStringDisplay as toStringShipmentState} from "../../../types/models/ShipmentState.d";
import TimberStorageLink from "../../timberStorage/TimberStorageDetail/TimberStorageLink.tsx";
import {mdiLinkVariant} from "@mdi/js";
import BuyerLink from "../../buyer/BuyerDetail/BuyerLink.tsx";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress.tsx";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useLocation} from "react-router";
import {t} from "@lingui/core/macro";
import {useGetGroupWithShipmentsQuery as useGetLogGroupWithShipmentsQuery} from "../../../apis/Log.api.ts";
import {useGetGroupWithShipmentsQuery as useGetCordwoodGroupWithShipmentsQuery} from "../../../apis/Cordwood.api.ts";

const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
        backgroundColor: theme.palette.action.hover,
        fontSize: 14,
    },
}));

const LogTotals = ({data}) => {
    return (
        <Paper elevation={2} sx={{textAlign: 'end'}}>
            <Typography variant="subtitle2"><Trans>Total Shipments</Trans>: {data.totalItems}</Typography>
            <Typography variant="subtitle2"><Trans>Total Logs</Trans>: {fixedFormat(data.metadata.totalLogs)}</Typography>
            <Typography variant="subtitle2"><Trans>Total Volume</Trans>: {fixedFormat(data.metadata.totalVolume)}</Typography>
        </Paper>
    )
}

export const GenericLogsShipments = ({filter}) => {

    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);

    const logShipmentData = useGetLogGroupWithShipmentsQuery({
        filter,
        list: {
            page, pageSize
        }
    })

    React.useEffect(() => {
        setPage(0);
    }, [filter, pageSize])

    return (
        <GenericRootShipments
            data={logShipmentData}
            title={t`Logs`}
            showCount={true}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            totals={<LogTotals data={logShipmentData.data}/>}
        />
    )
}
const CordwoodTotals = ({data}) => {
    return (
        <Paper elevation={2} sx={{textAlign: 'end'}}>
            <Typography variant="subtitle2"><Trans>Total Shipments</Trans>: {data.totalItems}</Typography>
            <Typography variant="subtitle2"><Trans>Total Volume</Trans>: {fixedFormat(data.metadata.totalVolume)}</Typography>
        </Paper>
    )
}

export const GenericCordwoodsShipments = ({filter}) => {

    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);

    const cordwoodShipmentData = useGetCordwoodGroupWithShipmentsQuery({
        filter,
        list: {
            page, pageSize
        }
    })

    React.useEffect(() => {
        setPage(0);
    }, [filter, pageSize])

    return (
        <GenericRootShipments
            data={cordwoodShipmentData}
            title={t`Cordwoods`}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            totals={<CordwoodTotals data={cordwoodShipmentData.data}/>}
        />
    )
}

const GenericRootShipments = ({data, title, showCount = false, page, pageSize, onPageChange, onPageSizeChange, totals}: {
    data: any,
    title: string,
    showCount: boolean,
    page: number,
    pageSize: number,
    onPageChange: (page: number) => void,
    onPageSizeChange: (pageSize: number) => void,
    totals: React.ReactElement
}) => {

    const theme = useTheme();
    const routeLocation = useLocation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    if (data.isLoading || data.isFetching || !data.data) {
        return (
            <Paper sx={{padding: "20px", justifyContent: "center", alignItems: "center", display: "flex"}}>
                <GradientCircularProgress/>
            </Paper>
        )
    }


    if (!data.data.items.length) {
        return (
            <Paper sx={{padding: "20px", justifyContent: "center", alignItems: "center", display: "flex"}}>
                <Typography variant="body1"><Trans>No shipments found</Trans></Typography>
            </Paper>
        )
    }

    const onClickUrl = (entity: Shipment) => routeLocation.pathname.endsWith('shipments') ?
        `${PrimaryShipment.fromEntity(entity).toPath()}`
        :
        `shipments/${PrimaryShipment.fromEntity(entity).toPath()}`

    return (
        <>
            <Stack direction="row" spacing={1}
                   sx={{
                       justifyContent: "space-between",
                       alignItems: "center",
                       padding: "10px",
                   }}>
                <Typography variant="h6">{title}</Typography>
                { totals }
            </Stack>
            <TableContainer>
                {isSmallScreen ?
                    <Table stickyHeader data-testid="Shipment-entity-list" size="small">
                        <TableHead>
                            <SmallTableHeaderRow>

                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'key')}
                                        direction={orderBys.find((item) => item.name === 'key')?.direction || 'asc'}
                                        onClick={() => toggleOrder('key')}
                                    >

                                        <Trans>Key</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>


                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'buyerId')}
                                        direction={orderBys.find((item) => item.name === 'buyerId')?.direction || 'asc'}
                                        onClick={() => toggleOrder('buyerId')}
                                    >

                                        <Trans>Buyer</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>
                                { showCount && <StyledTableCell key="count" align="right"><Trans>Logs Count</Trans></StyledTableCell> }
                                <StyledTableCell key="volume" align="right"><Trans>Volume (m&#xB3;)</Trans></StyledTableCell>
                            </SmallTableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {data.data.items.map((item) => (
                                <SmallTableRow
                                    hover={true}
                                    key={PrimaryShipment.fromEntity(item).toPath()}
                                >
                                    <TableCell
                                        scope="row"
                                        align="left"
                                    >
                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >

                                            <Typography component="div" sx={{fontWeight: 'bold',}}>
                                                {item.key ?
                                                    <>


                                                        {item.key || '-'}


                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                {item.reportKey &&

                                                    <>
                                                        <Trans>Report Key</Trans>:&nbsp;


                                                        {item.reportKey || '-'}


                                                    </>

                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                {item.cuttingId &&

                                                    <>
                                                        <Trans>Cutting</Trans>:&nbsp;

                                                        <CuttingName id={item.cuttingId}/>
                                                    </>

                                                }
                                            </Typography>


                                        </Link>
                                    </TableCell>


                                    <TableCell
                                        scope="row"
                                        align="left"
                                    >
                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >

                                            <Typography component="div" sx={{fontWeight: 'bold',}}>
                                                {item.buyerId ?
                                                    <>


                                                        <BuyerName id={item.buyerId}/>


                                                    </>
                                                    :
                                                    '-'
                                                }
                                            </Typography>


                                            <Typography component="div" variant="body1">
                                                {item.shipmentAt &&
                                                    <>
                                                        <Trans>at</Trans>:&nbsp;
                                                        {displayDateTime(item.shipmentAt)}
                                                    </>
                                                }
                                            </Typography>
                                            <Typography component="div" variant="body1">
                                                {item.state &&
                                                    <>
                                                        <Trans>State</Trans>:&nbsp;
                                                        {toStringShipmentState(item.state) || '-'}
                                                    </>
                                                }
                                            </Typography>
                                        </Link>
                                    </TableCell>

                                    { showCount && <TableCell key="count" align="right">{item.totalCount}</TableCell> }
                                    <TableCell key="volume" align="right">{fixedFormat(item.totalVolume)}</TableCell>

                                </SmallTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    :
                    <Table stickyHeader data-testid="Shipment-entity-list" size="small">
                        <TableHead>
                            <BigTableHeaderRow>


                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'key')}
                                        direction={orderBys.find((item) => item.name === 'key')?.direction || 'asc'}
                                        onClick={() => toggleOrder('key')}
                                    >

                                        <Trans>Key</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>


                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'reportKey')}
                                        direction={orderBys.find((item) => item.name === 'reportKey')?.direction || 'asc'}
                                        onClick={() => toggleOrder('reportKey')}
                                    >

                                        <Trans>Report Key</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>


                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'storageId')}
                                        direction={orderBys.find((item) => item.name === 'storageId')?.direction || 'asc'}
                                        onClick={() => toggleOrder('storageId')}
                                    >

                                        <Trans>Timber Storage</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>


                                <StyledTableCell align="center">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'shipmentAt')}
                                        direction={orderBys.find((item) => item.name === 'shipmentAt')?.direction || 'asc'}
                                        onClick={() => toggleOrder('shipmentAt')}
                                    >

                                        <Trans>Shipment At</Trans>

                                    </TableSortLabel>

                                </StyledTableCell>


                                <StyledTableCell align="left">

                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'buyerId')}
                                        direction={orderBys.find((item) => item.name === 'buyerId')?.direction || 'asc'}
                                        onClick={() => toggleOrder('buyerId')}
                                    >

                                        <Trans>Buyer</Trans>

                                    </TableSortLabel>
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                    <TableSortLabel
                                        active={orderBys.find((item) => item.name === 'state')}
                                        direction={orderBys.find((item) => item.name === 'state')?.direction || 'asc'}
                                        onClick={() => toggleOrder('state')}
                                    >
                                        <Trans>State</Trans>
                                    </TableSortLabel>
                                </StyledTableCell>
                                { showCount && <StyledTableCell key="count" align="right"><Trans>Logs Count</Trans></StyledTableCell> }
                                <StyledTableCell key="volume" align="right"><Trans>Volume
                                    (m&#xB3;)</Trans></StyledTableCell>

                            </BigTableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {data.data.items.map((item) => (
                                <BigTableRow
                                    hover={true}
                                    key={PrimaryShipment.fromEntity(item).toPath()}
                                >


                                    <TableCell
                                        scope="row"
                                        align="left"
                                    >
                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >
                                            {item.key || '-'}
                                        </Link>
                                    </TableCell>
                                    <TableCell
                                        scope="row"
                                        align="left"

                                    >
                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >
                                            {item.reportKey || '-'}
                                        </Link>

                                    </TableCell>


                                    <TableCell
                                        scope="row"
                                        align="left"
                                    >
                                        <TimberStorageLink id={item.storageId}
                                                           icon={<StyledMdiIcon d={mdiLinkVariant}/>}/>
                                    </TableCell>
                                    <TableCell
                                        scope="row"
                                        align="center"

                                    >

                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >


                                            {displayDateTime(item.shipmentAt)}


                                        </Link>

                                    </TableCell>


                                    <TableCell
                                        scope="row"
                                        align="left"

                                    >


                                        <BuyerLink id={item.buyerId} icon={<StyledMdiIcon d={mdiLinkVariant}/>}/>


                                    </TableCell>


                                    <TableCell
                                        scope="row"
                                        align="center"

                                    >

                                        <Link
                                            component={StyledFillNavLink}
                                            to={onClickUrl(item)}
                                            viewTransition={true}
                                        >


                                            {toStringShipmentState(item.state) || '-'}


                                        </Link>

                                    </TableCell>
                                    { showCount && <TableCell key="count" align="right">{item.totalCount}</TableCell> }
                                    <TableCell key="volume" align="right">{fixedFormat(item.totalVolume)}</TableCell>

                                </BigTableRow>
                            ))}
                        </TableBody>
                    </Table>
                }
            </TableContainer>
            <TablePagination
                width="100%"
                component="div"
                rowsPerPageOptions={[5, 10, 15, 25, 50, 200]}
                colSpan={5}
                count={data.data.totalItems}
                rowsPerPage={pageSize}
                showFirstButton={true}
                showLastButton={true}
                page={page}
                SelectProps={ {
                    inputProps: {
                        'aria-label': t`rows per page`,
                    },
                    native: true,
                } }
                onPageChange={(_, page) => {
                    onPageChange(page)
                }}
                onRowsPerPageChange={(e) => {
                    onPageSizeChange(Number(e.target.value))
                }}
            />
        </>)
}
